import React, { useMemo } from 'react';

import { SimpleGrid, Stack, Text, VStack } from '@chakra-ui/react';

import { AutocompleteCandidate, AutocompleteGroups } from '@/types/search';

import { AutocompleteJobCard } from './AutocompleteJobCard';
import { ResultCard } from './ResultCard';

interface SearchSectionConfig {
  key: keyof Pick<
    AutocompleteGroups,
    'contacts' | 'people' | 'engineers' | 'jobs'
  >;
  title: string;
}

const SECTIONS: SearchSectionConfig[] = [
  { key: 'contacts', title: 'Contacts' },
  { key: 'people', title: 'People' },
  { key: 'engineers', title: 'Engineers' },
  { key: 'jobs', title: 'Jobs' },
];

interface SearchResultSectionsProps {
  groups?: AutocompleteGroups;
  onSelect: (candidate: AutocompleteCandidate) => void;
}

export const SearchResultSections: React.FC<SearchResultSectionsProps> = ({
  groups,
  onSelect,
}) => {
  const visibleSections = useMemo(() => {
    if (!groups) return [];

    return SECTIONS.map(section => ({
      ...section,
      items: groups[section.key] ?? [],
    })).filter(section => section.items.length > 0);
  }, [groups]);

  if (visibleSections.length === 0) {
    return null;
  }

  return (
    <VStack align='stretch' gap={4} w='full' data-cy='global-search-results'>
      {visibleSections.map(section => (
        <Stack key={section.key} gap={0} align='stretch'>
          <Text
            fontSize='md'
            fontWeight='semibold'
            color='tabs.font-selected'
            borderBottom='2px solid'
            borderColor='tabs.font-selected'
            w='fit-content'
            pb={2}
            mb={2}
          >
            {section.title}
          </Text>
          <SimpleGrid
            columns={{ base: 1, xl: 3 }}
            spacing={{ base: 2, xl: 2.5 }}
            w='full'
          >
            {section.items.map(candidate =>
              candidate.type === 'job' ? (
                <AutocompleteJobCard
                  key={`${candidate.type}:${candidate.id}`}
                  candidate={candidate}
                  onOpenJob={() => onSelect(candidate)}
                />
              ) : (
                <ResultCard
                  key={`${candidate.type}:${candidate.id}`}
                  candidate={candidate}
                  onSelect={onSelect}
                />
              )
            )}
          </SimpleGrid>
        </Stack>
      ))}
    </VStack>
  );
};
